'use client';

import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/context/LanguageContext';
const ReferenceSection = () => {
  const [pos, setPos] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);   // 전체 슬라이드 트랙(5세트)
  const viewportRef = useRef<HTMLDivElement>(null);    // 보이는 영역
  const oneSetWidthRef = useRef(0);                    // “원본 1세트” 실제 너비
  const rafRef = useRef<number | null>(null);
  const { t } = useLanguage();
  const images = [
    '/images/reference/reference-1.png',
    '/images/reference/reference-2.png',
    '/images/reference/reference-3.png',
  ];

  // 무한 루프용으로 N세트 복제
  const REPEAT = 5;
  const slideImages = Array.from({ length: REPEAT * images.length }, (_, i) => images[i % images.length]);

  // 이동 속도 (px/frame). 화면이 작아지면 살짝 줄여서 부드럽게
  const baseSpeed = 0.8;

  useEffect(() => {
    const calcOneSetWidth = () => {
      const track = containerRef.current;
      if (!track) return;

      // 5세트 전체 scrollWidth를 REPEAT로 나눠 “한 세트의 실제 너비”를 얻는다.
      const total = track.scrollWidth;
      oneSetWidthRef.current = total / REPEAT;
    };

    // 초기 계산
    calcOneSetWidth();

    // 리사이즈/폰트로드/이미지로드 등 레이아웃 변화를 잡기 위해 ResizeObserver
    const ro = new ResizeObserver(() => {
      calcOneSetWidth();
    });

    if (viewportRef.current) ro.observe(viewportRef.current);
    if (containerRef.current) ro.observe(containerRef.current);

    // 이미지 로드 완료 후 재계산 (혹시 모를 사이즈 변동 대응)
    const imgs = Array.from(containerRef.current?.querySelectorAll('img') ?? []);
    let loaded = 0;
    imgs.forEach((img) => {
      if ((img as HTMLImageElement).complete) {
        loaded++;
      } else {
        img.addEventListener('load', () => {
          loaded++;
          if (loaded === imgs.length) calcOneSetWidth();
        });
      }
    });

    // prefers-reduced-motion 존중
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return () => ro.disconnect();

    const tick = () => {
      setPos((prev) => {
        const next = prev - baseSpeed;
        const limit = -oneSetWidthRef.current; // 한 세트만큼 왼쪽으로 밀면 0으로 리셋
        return next <= limit ? 0 : next;
      });
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, []);

  return (
    <div className="reference-section">
      <p className="title" dangerouslySetInnerHTML={{ __html: t.reference.title }} />

      <div className="reference-container">
        <div className="slider-viewport" ref={viewportRef}>
          <div
            ref={containerRef}
            className="image-slider-container"
            style={{
              transform: `translate3d(${pos}px, 0, 0)`,
              willChange: 'transform',
            }}
          >
            {slideImages.map((src, i) => (
              <div key={`${src}-${i}`} className="slide">
                <img src={src} alt={`reference-${(i % images.length) + 1}`} />
              </div>
            ))}
          </div>
        </div>

        <p className="description">
          {t.reference.description}
        </p>
      </div>
    </div>
  );
};

export default ReferenceSection;
